<template>
  <div class="min-h-screen bg-background text-text">
    <div class="container mx-auto px-4 py-8 md:py-10">
      <div class="mb-8">
        <p class="text-xs uppercase tracking-[0.22em] text-gold font-semibold">Messagerie</p>
        <h1 class="mt-2 text-3xl md:text-5xl font-bold text-primary">Messages privés</h1>
      </div>

      <div v-if="!isConnected" class="rounded-2xl border border-gold/20 bg-backgroundLight p-6 text-secondary">
        Vous devez être connecté pour accéder aux messages privés.
      </div>

      <div v-else class="grid gap-6 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside class="rounded-2xl border border-gold/20 bg-backgroundLight p-4">
          <h2 class="mb-4 text-lg font-bold text-primary">Conversations</h2>

          <div v-if="loadingConversations" class="text-sm text-secondary">Chargement...</div>
          <div v-else-if="!conversations.length" class="text-sm text-secondary">
            Aucune conversation pour le moment.
          </div>

          <div v-else class="space-y-3">
            <button
              v-for="conversation in conversations"
              :key="conversation.userId"
              type="button"
              @click="openConversation(conversation.userId)"
              :class="[
                'w-full rounded-xl border p-3 text-left transition-colors',
                selectedUserId === conversation.userId
                  ? 'border-gold bg-gold/10'
                  : 'border-secondary/15 bg-background hover:border-gold/40'
              ]"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="font-semibold text-primary">{{ conversation.username }}</span>
                <span v-if="conversation.unread" class="h-2.5 w-2.5 rounded-full bg-gold"></span>
              </div>
              <p class="mt-1 line-clamp-2 text-sm text-secondary">{{ conversation.lastMessage || 'Aucun message' }}</p>
            </button>
          </div>
        </aside>

        <section class="rounded-2xl border border-gold/20 bg-backgroundLight overflow-hidden">
          <div class="flex items-center justify-between border-b border-gold/20 px-5 py-4">
            <div>
              <p class="text-[10px] uppercase tracking-[0.15em] text-gold">Conversation</p>
              <h2 class="text-xl font-bold text-primary">{{ selectedUsername || 'Sélectionner un contact' }}</h2>
            </div>
          </div>

          <div class="h-[440px] overflow-y-auto p-4 space-y-3 bg-background/60">
            <div v-if="!selectedUserId" class="text-sm text-secondary italic">
              Sélectionnez une conversation pour afficher les messages.
            </div>
            <div v-else-if="!messages.length" class="text-sm text-secondary italic">
              Aucun message pour cette discussion.
            </div>

            <div
              v-for="message in messages"
              :key="message.id"
              class="max-w-[80%] rounded-2xl px-4 py-2 text-sm"
              :class="message.senderUserId === currentUserId ? 'ml-auto bg-gold text-primary' : 'bg-backgroundLight border border-secondary/15 text-text'"
            >
              <div class="mb-1 flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.12em] opacity-75">
                <span>{{ message.username }}</span>
                <span>{{ formatTime(message.createdAt) }}</span>
              </div>
              <p>{{ message.content }}</p>
            </div>
          </div>

          <div v-if="selectedUserId" class="border-t border-gold/20 p-4">
            <div class="flex gap-3">
              <input
                v-model="draft"
                type="text"
                placeholder="Écrire un message privé..."
                class="flex-1 rounded-full border border-secondary/20 bg-background px-4 py-3 text-sm text-text placeholder:text-secondary focus:outline-none focus:border-gold"
                @keyup.enter="sendMessage"
              />
              <button type="button" @click="sendMessage" class="btn-gold whitespace-nowrap">Envoyer</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import DMService from '@/services/dm.service'

const authStore = useAuthStore()
const conversations = ref([])
const loadingConversations = ref(true)
const selectedUserId = ref(null)
const selectedUsername = ref('')
const messages = ref([])
const draft = ref('')
const currentUserId = computed(() => authStore.user?.id || null)
const isConnected = computed(() => authStore.isLoggedIn || authStore.isAuthenticated)

async function loadConversations() {
  if (!isConnected.value) return

  try {
    loadingConversations.value = true
    const data = await DMService.getConversations()
    conversations.value = data || []

    if (selectedUserId.value) {
      const existing = conversations.value.find((item) => item.userId === selectedUserId.value)
      if (existing) {
        selectedUsername.value = existing.username
      }
    }
  } catch (error) {
    console.error('Erreur chargement conversations', error)
  } finally {
    loadingConversations.value = false
  }
}

async function openConversation(userId) {
  selectedUserId.value = userId
  const found = conversations.value.find((item) => item.userId === userId)
  selectedUsername.value = found?.username || ''

  try {
    const data = await DMService.getConversation(userId)
    messages.value = data || []
  } catch (error) {
    console.error('Erreur chargement conversation', error)
    messages.value = []
  }
}

async function sendMessage() {
  if (!selectedUserId.value || !draft.value.trim() || !isConnected.value) return

  try {
    const payload = await DMService.sendMessage(selectedUserId.value, draft.value)
    messages.value.push(payload)
    draft.value = ''
    await loadConversations()
  } catch (error) {
    console.error('Erreur envoi message privé', error)
  }
}

function formatTime(value) {
  if (!value) return ''
  const date = new Date(value)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

onMounted(() => {
  loadConversations()
})
</script>
